import type { EChartsOption } from 'echarts';

export type MnemonicGaugeConfig = {
    min?: number;
    max?: number;
    value?: number;
    unit?: string;
    name?: string;
    options?: Record<string, unknown>;
};

export const EMPTY_GAUGE_CONFIG: MnemonicGaugeConfig = {
    min: 0,
    max: 100,
    value: 0,
    unit: '',
    name: '',
};

function parseGaugeValue(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function resolveGaugeMetrics(size: number) {
    const scale = Math.max(1, size / 280);

    return {
        axisLineWidth: Math.round(14 * scale),
        axisTickLength: Math.round(6 * scale),
        splitLineLength: Math.round(16 * scale),
        axisLabelDistance: Math.round(30 * scale),
        axisLabelFontSize: Math.round(11 * scale),
        titleFontSize: Math.round(13 * scale),
        detailFontSize: Math.round(18 * scale),
        pointerWidth: Math.max(4, Math.round(5 * scale)),
    };
}

export function toGaugeEChartsOption(config: MnemonicGaugeConfig, fallbackName?: string, size = 280): EChartsOption {
    const min = config.min ?? EMPTY_GAUGE_CONFIG.min!;
    const max = config.max ?? EMPTY_GAUGE_CONFIG.max!;
    const value = parseGaugeValue(config.value);
    const unit = config.unit ?? '';
    const name = config.name?.trim() || fallbackName?.trim() || '';
    const customOptions = (config.options ?? {}) as EChartsOption;
    const detailFormatter = unit ? `{value} ${unit}` : '{value}';
    const metrics = resolveGaugeMetrics(size);

    return {
        ...customOptions,
        animation: false,
        series: [
            {
                type: 'gauge',
                min,
                max,
                splitNumber: 10,
                radius: '88%',
                startAngle: 210,
                endAngle: -30,
                center: ['50%', '56%'],
                progress: {
                    show: true,
                    width: metrics.axisLineWidth,
                    roundCap: true,
                },
                axisLine: {
                    roundCap: true,
                    lineStyle: {
                        width: metrics.axisLineWidth,
                        color: [
                            [0.3, '#67e0e3'],
                            [0.7, '#37a2da'],
                            [1, '#fd666d'],
                        ],
                    },
                },
                pointer: {
                    show: true,
                    length: '58%',
                    width: metrics.pointerWidth,
                    itemStyle: {
                        color: 'auto',
                    },
                },
                axisTick: {
                    distance: -(metrics.axisLineWidth + 8),
                    length: metrics.axisTickLength,
                    lineStyle: {
                        color: '#8a8f9c',
                        width: 1,
                    },
                },
                splitLine: {
                    distance: -(metrics.axisLineWidth + 8),
                    length: metrics.splitLineLength,
                    lineStyle: {
                        color: '#8a8f9c',
                        width: 2,
                    },
                },
                axisLabel: {
                    color: '#5c6370',
                    distance: metrics.axisLabelDistance,
                    fontSize: metrics.axisLabelFontSize,
                },
                title: {
                    show: Boolean(name),
                    offsetCenter: [0, '72%'],
                    fontSize: metrics.titleFontSize,
                    color: '#171b2c',
                },
                detail: {
                    valueAnimation: false,
                    formatter: detailFormatter,
                    color: '#171b2c',
                    fontSize: metrics.detailFontSize,
                    fontWeight: 600,
                    offsetCenter: [0, '38%'],
                },
                data: [{ value, name }],
                animation: false,
            },
        ],
    };
}
